import React, { useState, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { satsueiDataTorokuService } from '../../services/satsueiDataTorokuService'
import { useApiError } from '../../../shared/hooks/useApiError'
import MessageDialog from '../../../shared/ui/MessageDialog'
import CameraSettingsModal from './CameraSettingsModal'

// Convert a data URL (e.g. from canvas.toDataURL) to a File for upload
const dataURLToFile = (dataUrl, filename) => {
  const arr = dataUrl.split(',')
  const mime = (arr[0].match(/:(.*?);/) || [])[1] || 'image/png'
  const bstr = atob(arr[1] || '')
  const u8arr = new Uint8Array(bstr.length)
  for (let i = 0; i < bstr.length; i++) {
    u8arr[i] = bstr.charCodeAt(i)
  }
  return new File([u8arr], filename, { type: mime })
}

const CameraModal = ({ isOpen, onClose, objectId, imageFolderId, onSave }) => {
  const [viewportLayout, setViewportLayout] = useState('4') // '1', '2', or '4'
  const [currentCameraIndex, setCurrentCameraIndex] = useState(0) // For 1-viewport carousel
  const [cameras, setCameras] = useState([])
  const [capturedImages, setCapturedImages] = useState([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const viewportImgRefs = React.useRef([])

  const {
    showErrorDialog,
    errorMessage,
    messageType,
    handleApiError,
    handleApiResponse,
    closeErrorDialog,
    showSuccessMessage
  } = useApiError()

  useEffect(() => {
    if (isOpen) {
      loadCameraList()
      // Reset state when modal opens
      setCapturedImages([])
      setCurrentCameraIndex(0)
    } else {
      // When modal closes, clear stream img refs so we don't hold refs to unmounted nodes
      viewportImgRefs.current = []
    }
  }, [isOpen])

  // Reload camera list when settings modal updates cameras
  useEffect(() => {
    if (isOpen) {
      // Listen for storage changes (when CameraSettingsModal updates cameras)
      const handleStorageChange = () => {
        loadCameraList()
      }
      window.addEventListener('storage', handleStorageChange)
      // Also check periodically (since same-tab storage events don't fire)
      const interval = setInterval(() => {
        loadCameraList()
      }, 1000)
      
      return () => {
        window.removeEventListener('storage', handleStorageChange)
        clearInterval(interval)
      }
    }
  }, [isOpen])

  // Capture current frame from a viewport <img> (MJPEG stream) to a data URL.
  // Returns null if the canvas is tainted (cross-origin stream without CORS) or if draw/export fails.
  const captureFrameFromImg = (img) => {
    if (!img || !img.complete || img.naturalWidth === 0) return null
    try {
      const canvas = document.createElement('canvas')
      canvas.width = img.naturalWidth
      canvas.height = img.naturalHeight
      const ctx = canvas.getContext('2d')
      if (!ctx) return null
      ctx.drawImage(img, 0, 0)
      return canvas.toDataURL('image/png')
    } catch {
      return null
    }
  }

  // Generate placeholder image for testing / fallback when stream capture fails
  const generatePlaceholderImage = (cameraName, cameraId) => {
    const canvas = document.createElement('canvas')
    canvas.width = 640
    canvas.height = 480
    const ctx = canvas.getContext('2d')
    
    // Background
    ctx.fillStyle = '#1f2937'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    
    // Text
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 32px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(cameraName || `カメラ${cameraId}`, canvas.width / 2, canvas.height / 2 - 40)
    
    ctx.font = '20px Arial'
    ctx.fillText('撮影時刻: ' + new Date().toLocaleTimeString('ja-JP'), canvas.width / 2, canvas.height / 2 + 20)
    ctx.fillText('(プレースホルダー画像)', canvas.width / 2, canvas.height / 2 + 50)
    
    return canvas.toDataURL('image/png')
  }

  const loadCameraList = async () => {
    try {
      // PLACEHOLDER: Use localStorage for now since backend is not ready
      // TODO: Uncomment below and remove placeholder code when backend is ready
      /*
      const response = await satsueiDataTorokuService.getCameraList()
      
      // Check if response has error
      if (response && response.returnCode && response.returnCode !== '200') {
        if (handleApiResponse(response, 'カメラリストの取得に失敗しました')) {
          // Use placeholder cameras on error
          setCameras([
            { cameraId: '1', cameraName: 'カメラ1' },
            { cameraId: '2', cameraName: 'カメラ2' },
            { cameraId: '3', cameraName: 'カメラ3' },
            { cameraId: '4', cameraName: 'カメラ4' }
          ])
          return
        }
      }

      if (response && response.cameraList) {
        setCameras(response.cameraList)
      } else {
        // Placeholder cameras for development
        setCameras([
          { cameraId: '1', cameraName: 'カメラ1' },
          { cameraId: '2', cameraName: 'カメラ2' },
          { cameraId: '3', cameraName: 'カメラ3' },
          { cameraId: '4', cameraName: 'カメラ4' }
        ])
      }
      */
      
      // Load from localStorage (shared with CameraSettingsModal)
      const savedCameras = localStorage.getItem('cameraList')
      if (savedCameras) {
        setCameras(JSON.parse(savedCameras))
      } else {
        // Default placeholder cameras
        const defaultCameras = [
          { cameraId: '1', cameraName: 'カメラ1', cameraKey: 'key1', connectionUrl: 'https://stg-mic-cam.gkun.net/MIC-CAM-A01001/video_feed' },
          { cameraId: '2', cameraName: 'カメラ2', cameraKey: 'key2', connectionUrl: 'http://192.168.1.101' },
          { cameraId: '3', cameraName: 'カメラ3', cameraKey: 'key3', connectionUrl: 'http://192.168.1.102' },
          { cameraId: '4', cameraName: 'カメラ4', cameraKey: 'key4', connectionUrl: 'http://192.168.1.103' }
        ]
        setCameras(defaultCameras)
        localStorage.setItem('cameraList', JSON.stringify(defaultCameras))
      }
    } catch (error) {
      // Use default cameras on error
      const defaultCameras = [
        { cameraId: '1', cameraName: 'カメラ1' },
        { cameraId: '2', cameraName: 'カメラ2' },
        { cameraId: '3', cameraName: 'カメラ3' },
        { cameraId: '4', cameraName: 'カメラ4' }
      ]
      setCameras(defaultCameras)
    }
  }

  const handleCapture = async () => {
    if (!objectId) {
      handleApiResponse({ returnCode: '400', message: 'オブジェクトIDが必要です' }, '入力エラー')
      return
    }

    try {
      setCapturedImages([]);
      setIsCapturing(true)
      const visibleCameras = getVisibleCameras()
      if (visibleCameras.length === 0 || visibleCameras.every(c => !c)) {
        handleApiResponse({ returnCode: '400', message: 'カメラが登録されていません' }, '入力エラー')
        return
      }

      let hadCorsFallback = false
      const newImages = visibleCameras.map((camera, index) => {
        const cameraId = camera?.cameraId ?? `slot-${index}`
        const cameraName = camera?.cameraName ?? `カメラ${index + 1}`
        let imageData
        const imgEl = viewportImgRefs.current[index]
        if (camera?.connectionUrl && imgEl) {
          imageData = captureFrameFromImg(imgEl)
          if (!imageData) hadCorsFallback = true
        }
        if (!imageData) {
          imageData = generatePlaceholderImage(camera?.cameraName ?? '未割当', cameraId)
        }
        return {
          cameraId,
          cameraName,
          imageData,
          timestamp: new Date().toISOString()
        }
      })

      setCapturedImages(prev => [...prev, ...newImages])
      if (hadCorsFallback) {
        showSuccessMessage(`${newImages.length}枚の画像を取得しました（別オリジンの映像はCORSのためプレースホルダーです）`)
      } else {
        showSuccessMessage(`${newImages.length}枚の画像を取得しました`)
      }
    } catch (error) {
      handleApiError(error, '画像の取得に失敗しました')
    } finally {
      setIsCapturing(false)
    }
  }

  const handleSave = async () => {
    if (capturedImages.length === 0) {
      handleApiResponse({ returnCode: '400', message: '保存する画像がありません' }, '入力エラー')
      return
    }

    if (!objectId) {
      handleApiResponse({ returnCode: '400', message: 'オブジェクトIDが必要です' }, '入力エラー')
      return
    }

    if (imageFolderId === 99) {
      handleApiResponse({ returnCode: '400', message: '「全フォルダ表示」を選択している時はアップロードが出来ません' }, '入力エラー')
      return
    }

    try {
      setIsSaving(true)
      let uploadSuccess = true

      for (let i = 0; i < capturedImages.length; i++) {
        const dataUrl = capturedImages[i]
        const file = dataURLToFile(dataUrl, `camera-capture-${i + 1}.png`)

        const formData = new FormData()
        formData.append('objectId', objectId)
        formData.append('inputFile', file)
        formData.append('fileName', file.name)
        formData.append('fileSize', file.size)
        if (imageFolderId != null && imageFolderId !== 99) {
          formData.append('imageFolderId', imageFolderId)
        }

        const response = await satsueiDataTorokuService.uploadFile('uploadImage', formData)

        if (response) {
          const hasError = handleApiResponse(response, '画像の保存に失敗しました')
          if (hasError) {
            uploadSuccess = false
            break
          }
        }
      }

      if (uploadSuccess) {
        showSuccessMessage('画像の保存が完了しました')
        setCapturedImages([])
        if (onSave) {
          onSave()
        }
      }
    } catch (error) {
      handleApiError(error, '画像の保存に失敗しました')
    } finally {
      setIsSaving(false)
    }
  }

  // For 2-view: number of fixed pairs (1+2, 3+4, ...). Pair start indices are 0, 2, 4, ...
  const getTwoViewPairCount = () => Math.max(1, Math.ceil(cameras.length / 2))

  const handlePrevCamera = () => {
    if (String(viewportLayout) === '2' && cameras.length > 2) {
      const numPairs = getTwoViewPairCount()
      const wrap = numPairs * 2
      setCurrentCameraIndex(prev => (prev - 2 + wrap) % wrap)
    } else {
      setCurrentCameraIndex(prev => (prev > 0 ? prev - 1 : cameras.length - 1))
    }
  }

  const handleNextCamera = () => {
    if (String(viewportLayout) === '2' && cameras.length > 2) {
      const numPairs = getTwoViewPairCount()
      const wrap = numPairs * 2
      setCurrentCameraIndex(prev => (prev + 2) % wrap)
    } else {
      setCurrentCameraIndex(prev => (prev < cameras.length - 1 ? prev + 1 : 0))
    }
  }

  const getVisibleCameras = () => {
    const layout = String(viewportLayout)
    if (layout === '1') {
      return cameras.length > 0 ? [cameras[currentCameraIndex]] : []
    }
    if (layout === '2') {
      if (cameras.length === 0) return []
      if (cameras.length === 1) return [cameras[0], null]
      // Fixed pairs: (0,1), (2,3), (4,5), ... Second slot may be empty if odd camera count.
      const firstIndex = currentCameraIndex
      const secondIndex = currentCameraIndex + 1
      return [
        cameras[firstIndex],
        secondIndex < cameras.length ? cameras[secondIndex] : null
      ]
    }
    return cameras.slice(0, 4)
  }

  const getScreenshotLayout = () => {
    if (viewportLayout === '1') {
      return 'grid-cols-1'
    } else if (viewportLayout === '2') {
      return 'grid-cols-2'
    } else {
      return 'grid-cols-2'
    }
  }

  const renderViewport = (camera, index, setImgRef) => {
    const streamUrl = camera?.connectionUrl
    const hasStream = streamUrl && streamUrl.trim() !== ''

    return (
      <div
        key={camera?.cameraId ?? `vp-${index}`}
        className="relative aspect-video bg-gray-800 rounded border border-gray-300 flex items-center justify-center overflow-hidden"
      >
        {hasStream ? (
          <>
            <img
              ref={(el) => { if (setImgRef) setImgRef(el) }}
              src={streamUrl}
              alt={camera?.cameraName || `カメラ${index + 1}`}
              className="w-full h-full object-contain bg-black"
              //crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              onError={(e) => {
                e.target.style.display = 'none'
                const fallback = e.target.nextElementSibling
                if (fallback) fallback.classList.remove('hidden')
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800 hidden" aria-hidden="true">
              <div className="text-white text-sm text-center px-2">
                <p>映像を読み込めません</p>
                <p className="text-xs mt-1 opacity-80">URLを確認するか、カメラの設定を確認してください</p>
              </div>
            </div>
          </>
        ) : (
          <div className="text-white text-sm text-center px-2">
            <p>{camera ? `カメラの映像${index + 1}` : '—'}</p>
            <p className="text-xs mt-1 opacity-80">{camera ? '接続URLが未設定です' : '未割当'}</p>
          </div>
        )}
        {camera && (
          <div className="absolute bottom-2 left-2 text-white text-xs bg-black bg-opacity-50 px-2 py-1 rounded">
            {camera.cameraName || `カメラ${index + 1}`}
          </div>
        )}
      </div>
    )
  }

  const renderViewports = () => {
    const visibleCameras = getVisibleCameras()
    const layout = String(viewportLayout)

    if (layout === '1') {
      const currentCamera = visibleCameras[0] || cameras[currentCameraIndex]
      return (
        <div key="layout-1" className="relative w-full">
          {cameras.length > 1 && (
            <>
              <button
                onClick={handlePrevCamera}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                aria-label="前のカメラ"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNextCamera}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                aria-label="次のカメラ"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          {renderViewport(currentCamera, currentCameraIndex, (el) => { viewportImgRefs.current[0] = el })}
        </div>
      )
    }
    if (layout === '2') {
      const showPairNav = cameras.length > 2
      return (
        <div key="layout-2" className="relative w-full">
          {showPairNav && (
            <>
              <button
                onClick={handlePrevCamera}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                aria-label="前の2台"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={handleNextCamera}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                aria-label="次の2台"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <div className="grid grid-cols-2 gap-2">
            {[0, 1].map(index => renderViewport(visibleCameras[index], index, (el) => { viewportImgRefs.current[index] = el }))}
          </div>
        </div>
      )
    }
    return (
      <div key="layout-4" className="grid grid-cols-2 grid-rows-2 gap-2 w-full">
        {[0, 1, 2, 3].map(index => renderViewport(visibleCameras[index], index, (el) => { viewportImgRefs.current[index] = el }))}
      </div>
    )
  }

  const renderScreenshots = () => {
    if (capturedImages.length === 0) {
      return (
        <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
          撮影された画像がありません
        </div>
      )
    }

    const layoutClass = getScreenshotLayout()
    const displayImages = viewportLayout === '1' 
      ? capturedImages.slice(-1) // Show only latest for 1-viewport
      : capturedImages.slice(-parseInt(viewportLayout)) // Show last N images

    return (
      <div className={`grid ${layoutClass} gap-2`}>
        {displayImages.map((image, index) => (
          <div
            key={`${image.cameraId}-${image.timestamp}-${index}`}
            className="relative aspect-video bg-gray-100 rounded border border-gray-300 overflow-hidden"
          >
            {image.imageData ? (
              <img
                src={typeof image.imageData === 'string' ? image.imageData : URL.createObjectURL(image.imageData)}
                alt={`${image.cameraName || 'Camera'} - ${new Date(image.timestamp).toLocaleTimeString()}`}
                className="w-full h-full object-contain"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 text-xs">
                画像データなし
              </div>
            )}
            <div className="absolute bottom-1 left-1 text-xs bg-black bg-opacity-50 text-white px-2 py-1 rounded">
              {image.cameraName || `カメラ${index + 1}`}
            </div>
          </div>
        ))}
      </div>
    )
  }

  const handleClose = () => {
    // Stop video feeds immediately so the browser stops requesting the stream
    const refs = viewportImgRefs.current
    if (refs && refs.length) {
      refs.forEach((img) => {
        if (img && typeof img.src === 'string') img.removeAttribute('src')
      })
      viewportImgRefs.current = []
    }
    setCapturedImages([])
    setCurrentCameraIndex(0)
    setShowSettingsModal(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="camera-modal">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-7xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">カメラ撮影</h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-modal-button"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid grid-cols-10 gap-4">
            {/* Left Panel - Camera Viewports (60%) */}
            <div className="col-span-6 space-y-4">
              {/* Viewport Layout Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  表示レイアウト
                </label>
                <select
                  value={viewportLayout}
                  onChange={(e) => {
                    const newLayout = e.target.value
                    setViewportLayout(newLayout)
                    if (String(newLayout) === '2' && cameras.length > 2) {
                      const numPairs = Math.ceil(cameras.length / 2)
                      setCurrentCameraIndex(prev => Math.min(Math.floor(prev / 2) * 2, (numPairs - 1) * 2))
                    } else {
                      setCurrentCameraIndex(0)
                    }
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                >
                  <option value="1">1画面</option>
                  <option value="2">2画面</option>
                  <option value="4">4画面</option>
                </select>
              </div>

              {/* Viewports - key forces full remount when layout changes so only one layout is ever shown */}
              <div className="space-y-2" key={`viewports-${viewportLayout}`}>
                {renderViewports()}
              </div>
            </div>

            {/* Right Panel - Controls and Screenshots (40%) */}
            <div className="col-span-4 space-y-4">
              {/* Camera Settings Button */}
              <button
                onClick={() => setShowSettingsModal(true)}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                カメラの設定
              </button>

              {/* Capture Button */}
              <button
                onClick={handleCapture}
                disabled={isCapturing || cameras.length === 0}
                className={`w-full px-4 py-2 rounded text-sm font-medium transition-colors ${
                  isCapturing || cameras.length === 0
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isCapturing ? '撮影中...' : '撮影'}
              </button>

              {/* Screenshot Display Area */}
              <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-2">撮影画像</h4>
                <div className="max-h-64 overflow-y-auto">
                  {renderScreenshots()}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={isSaving || capturedImages.length === 0}
                className={`w-full px-4 py-2 rounded text-sm font-medium transition-colors ${
                  isSaving || capturedImages.length === 0
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Settings Modal */}
      <CameraSettingsModal
        isOpen={showSettingsModal}
        onClose={() => {
          setShowSettingsModal(false)
          // Reload camera list when settings modal closes
          loadCameraList()
        }}
        onCameraListUpdate={loadCameraList}
      />

      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title={
          messageType === 'error' ? 'エラー' :
            messageType === 'success' ? '成功' :
              messageType === 'warning' ? '警告' :
                '情報'
        }
        message={errorMessage}
        type={messageType}
        buttonText="閉じる"
        data-testid="message-dialog"
      />
    </>
  )
}

export default CameraModal

